import React, { useEffect, useRef, useState } from 'react';
import { 
  Box, 
  Typography, 
  makeStyles, 
  Paper,
  Chip,
  Grid,
  FormControlLabel,
  Checkbox,
  Button,
  CircularProgress
} from '@material-ui/core';
import { 
  LocalFlorist, 
  Warning, 
  LocationOn,
  MyLocation,
  Layers,
  CheckCircle
} from '@material-ui/icons';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const useStyles = makeStyles((theme) => ({
  mapWrapper: {
    position: 'relative',
    height: 600,
    width: '100%'
  },
  mapContainer: {
    height: '100%',
    width: '100%',
    position: 'relative',
    zIndex: 1
  },
  mapControls: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 1000,
    backgroundColor: 'white',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[3],
    maxWidth: 300
  },
  legend: {
    position: 'absolute',
    bottom: theme.spacing(2),
    left: theme.spacing(2),
    zIndex: 1000,
    backgroundColor: 'white',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[3]
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0
    }
  },
  legendIcon: {
    marginRight: theme.spacing(1)
  },
  statsBox: {
    position: 'absolute',
    top: theme.spacing(2),
    left: theme.spacing(2),
    zIndex: 1000,
    backgroundColor: 'white',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[3]
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    zIndex: 1100
  }
}));

// Custom icon definitions
const createCustomIcon = (color, type) => {
  const svgIcon = type === 'dead' 
    ? `<svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.5 0C5.6 0 0 5.6 0 12.5C0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0Z" fill="${color}"/>
        <circle cx="12.5" cy="12.5" r="8" fill="white"/>
        <path d="M12.5 7 L12.5 18 M7 12.5 L18 12.5" stroke="${color}" stroke-width="2"/>
      </svg>`
    : `<svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.5 0C5.6 0 0 5.6 0 12.5C0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0Z" fill="${color}"/>
        <circle cx="12.5" cy="12.5" r="8" fill="white"/>
        <path d="M12.5 7 L12.5 13 M12.5 16 L12.5 18" stroke="${color}" stroke-width="2"/>
      </svg>`;

  return L.divIcon({
    html: svgIcon,
    iconSize: [25, 41],
    iconAnchor: [12.5, 41],
    popupAnchor: [0, -41],
    className: 'custom-plant-marker'
  });
};

const deadPlantIcon = createCustomIcon('#d32f2f', 'dead');
const dyingPlantIcon = createCustomIcon('#f57c00', 'dying');
const completedIcon = createCustomIcon('#388e3c', 'completed');

export default function PlantTrackingMap({ plants, onPlantClick }) {
  const classes = useStyles();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    showDead: true,
    showDying: true,
    showCompleted: true,
    showPending: true
  });
  const [mapStats, setMapStats] = useState({
    total: 0,
    dead: 0,
    dying: 0,
    visible: 0
  });

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Create map instance
    const map = L.map(mapRef.current).setView([33.448457, -112.074037], 11); // Phoenix area default

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19
    }).addTo(map);

    mapInstanceRef.current = map;
    setLoading(false);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Update markers when plants or filters change
  useEffect(() => {
    if (!mapInstanceRef.current || !plants) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    const bounds = L.latLngBounds();
    let stats = { total: 0, dead: 0, dying: 0, visible: 0 };
    
    plants.forEach(plant => {
      // Skip if no valid location
      if (!plant.location?.latitude || !plant.location?.longitude) return;
      if (plant.location.latitude === 0 && plant.location.longitude === 0) return;

      stats.total++;
      if (plant.type === 'dead') stats.dead++;
      if (plant.type === 'dying') stats.dying++;

      // Apply filters
      const isCompleted = plant.state === 'COMPLETED' || plant.state === 'completed';
      
      let shouldShow = false;
      if (plant.type === 'dead' && filters.showDead) shouldShow = true;
      if (plant.type === 'dying' && filters.showDying) shouldShow = true;
      if (isCompleted && filters.showCompleted) shouldShow = true;
      if (!isCompleted && filters.showPending) shouldShow = true;

      if (!shouldShow) return;

      stats.visible++;

      // Select appropriate icon
      let icon = plant.type === 'dead' ? deadPlantIcon : dyingPlantIcon;
      if (isCompleted) icon = completedIcon;

      // Create marker
      const marker = L.marker([plant.location.latitude, plant.location.longitude], { icon })
        .addTo(mapInstanceRef.current);

      // Create popup content
      const popupContent = `
        <div style="min-width: 200px;">
          <h4 style="margin: 0 0 8px 0;">
            ${plant.type === 'dead' ? '🪦 Dead' : '⚠️ Dying'} Plant
          </h4>
          <p style="margin: 4px 0;"><strong>ID:</strong> ${plant.plantId}</p>
          <p style="margin: 4px 0;"><strong>Status:</strong> ${plant.state}</p>
          <p style="margin: 4px 0;"><strong>Job Site:</strong> ${plant.jobSite?.name || 'N/A'}</p>
          <p style="margin: 4px 0;"><strong>Reported:</strong> ${new Date(plant.timestamps?.created).toLocaleDateString()}</p>
          ${plant.notes ? `<p style="margin: 4px 0;"><strong>Notes:</strong> ${plant.notes}</p>` : ''}
          <button 
            onclick="window.dispatchEvent(new CustomEvent('plant-click', { detail: '${plant._id}' }))"
            style="margin-top: 8px; padding: 4px 8px; background: #1976d2; color: white; border: none; border-radius: 4px; cursor: pointer;"
          >
            View Details
          </button>
        </div>
      `;

      marker.bindPopup(popupContent);
      markersRef.current.push(marker);
      bounds.extend([plant.location.latitude, plant.location.longitude]);
    });

    // Fit map to bounds if we have markers
    if (markersRef.current.length > 0) {
      mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
    }

    setMapStats(stats);
  }, [plants, filters]);

  // Handle plant click from popup
  useEffect(() => {
    const handlePlantClick = (e) => {
      const plant = plants.find(p => p._id === e.detail);
      if (plant && onPlantClick) {
        onPlantClick(plant);
      }
    };

    window.addEventListener('plant-click', handlePlantClick);
    return () => window.removeEventListener('plant-click', handlePlantClick);
  }, [plants, onPlantClick]);

  const handleFilterChange = (filterName) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  const centerOnPlants = () => {
    if (markersRef.current.length > 0) {
      const bounds = L.latLngBounds();
      markersRef.current.forEach(marker => {
        bounds.extend(marker.getLatLng());
      });
      mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
    }
  };

  return (
    <Paper className={classes.mapWrapper}>
      {loading && (
        <Box className={classes.loadingOverlay}>
          <CircularProgress />
        </Box>
      )}
      
      <div ref={mapRef} className={classes.mapContainer} />

      {/* Map Stats */}
      <Paper className={classes.statsBox}>
        <Typography variant="subtitle2" gutterBottom>
          Plant Statistics
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Chip 
              size="small" 
              label={`Total: ${mapStats.total}`} 
              color="primary"
            />
          </Grid>
          <Grid item xs={6}>
            <Chip 
              size="small" 
              label={`Dead: ${mapStats.dead}`} 
              style={{ backgroundColor: '#ffcdd2', color: '#b71c1c' }}
            />
          </Grid>
          <Grid item xs={6}>
            <Chip 
              size="small" 
              label={`Dying: ${mapStats.dying}`} 
              style={{ backgroundColor: '#ffe0b2', color: '#e65100' }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">
              Showing {mapStats.visible} of {mapStats.total}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Map Controls */}
      <Paper className={classes.mapControls}>
        <Typography variant="subtitle2" gutterBottom>
          Filter Plants
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={filters.showDead}
              onChange={() => handleFilterChange('showDead')}
              size="small"
            />
          }
          label="Show Dead Plants"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={filters.showDying}
              onChange={() => handleFilterChange('showDying')}
              size="small"
            />
          }
          label="Show Dying Plants"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={filters.showCompleted}
              onChange={() => handleFilterChange('showCompleted')}
              size="small"
            />
          }
          label="Show Completed"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={filters.showPending}
              onChange={() => handleFilterChange('showPending')}
              size="small"
            />
          }
          label="Show Pending"
        />
        <Button
          variant="outlined"
          size="small"
          fullWidth
          startIcon={<MyLocation />}
          onClick={centerOnPlants}
          style={{ marginTop: 8 }}
        >
          Center on Plants
        </Button>
      </Paper>

      {/* Legend */}
      <Paper className={classes.legend}>
        <Typography variant="subtitle2" gutterBottom>
          Legend
        </Typography>
        <Box className={classes.legendItem}>
          <LocalFlorist className={classes.legendIcon} style={{ color: '#d32f2f' }} />
          <Typography variant="body2">Dead Plant</Typography>
        </Box>
        <Box className={classes.legendItem}>
          <Warning className={classes.legendIcon} style={{ color: '#f57c00' }} />
          <Typography variant="body2">Dying Plant</Typography>
        </Box>
        <Box className={classes.legendItem}>
          <CheckCircle className={classes.legendIcon} style={{ color: '#388e3c' }} />
          <Typography variant="body2">Completed</Typography>
        </Box>
      </Paper>
    </Paper>
  );
}